import { DeliveryProviderPortal } from '@bolteu/bolt-server-api-delivery-provider-portal';
import { HttpClientBoltServer } from '@bolteu/http-client-bolt-server';
import { Hooks, ShardedSelectors } from '@bolteu/react-api-gateway/es';

import config from '../config';
import { HttpClientBoltServerConstraints } from '../types/types';
import { CampaignDeliveryProvider } from './gen/CampaignDeliveryProvider';
import { DeliveryProviderAuthApi } from './gen/DeliveryProviderAuthApi';
import { DeliveryProviderNoAuthApi } from './gen/DeliveryProviderNoAuthApi';
import { DeliveryProviderPortalAuthApi } from './gen/DeliveryProviderPortalAuthApi';

const sharedDefaults = {
  accessTokenBlockingTimeoutBeforeExpiryInSeconds:
    config.accessTokenBlockingTimeoutBeforeExpiryInSeconds,
  accessTokenRefreshTimeoutBeforeExpiryInSeconds:
    config.accessTokenRefreshTimeoutBeforeExpiryInSeconds,
};

export class ApiClient {
  public readonly session: DeliveryProviderNoAuthApi;
  public readonly provider: DeliveryProviderAuthApi;
  public readonly portal: DeliveryProviderPortalAuthApi;
  public readonly promotions: CampaignDeliveryProvider;

  // API generated by bolt-server-api-delivery-provider-portal
  public readonly providerPortalApi: DeliveryProviderPortal.ApiClient<any>;

  constructor(httpClientBoltServer: HttpClientBoltServer<HttpClientBoltServerConstraints>) {
    this.session = new DeliveryProviderNoAuthApi({
      baseUrl: config.apiBaseUrl,
      serviceId: 'delivery-provider',
    });
    this.provider = new DeliveryProviderAuthApi({
      ...sharedDefaults,
      baseUrl: config.apiBaseUrl,
      serviceId: 'delivery-provider',
    });
    this.portal = new DeliveryProviderPortalAuthApi({
      ...sharedDefaults,
      baseUrl: config.apiBaseUrl,
      serviceId: 'delivery-provider-portal',
    });
    this.promotions = new CampaignDeliveryProvider({
      ...sharedDefaults,
      baseUrl: config.apiBaseUrl,
      serviceId: 'delivery-campaign-provider',
    });

    this.providerPortalApi = new DeliveryProviderPortal.ApiClient({
      apiClientOptions: { virtualPathsEnabled: false },
      httpClient: httpClientBoltServer,
    });
  }

  private getInitSettings({ hooks, selectors }: { hooks: Hooks; selectors: ShardedSelectors }) {
    const { getDefaultQueryParams, getAccessToken, getShardingKey } = selectors;

    // Probably should reconsider API of NoAuth Gateway
    const noAuthSettings = {
      hooks: {
        preRequestHook: hooks.preRequestHook,
        updateAccessToken: hooks.updateAccessToken,
      },
      selectors: {
        getDefaultQueryParams,
        getAccessToken: () => '',
      },
    };

    const unshardedSettings = {
      hooks,
      selectors: {
        getDefaultQueryParams,
        getAccessToken,
      },
    };

    const shardedSettings = {
      ...unshardedSettings,
      selectors: {
        ...unshardedSettings.selectors,
        getShardingKey,
      },
    };

    return {
      noAuthSettings,
      unshardedSettings,
      shardedSettings,
    };
  }

  public init({ hooks, selectors }: { hooks: Hooks; selectors: ShardedSelectors }) {
    const { unshardedSettings, noAuthSettings } = this.getInitSettings({
      hooks,
      selectors,
    });

    // NoAuth Services
    this.session.init(noAuthSettings);

    // Unsharded Services
    this.provider.init(unshardedSettings);
    this.portal.init(unshardedSettings);
    this.promotions.init(unshardedSettings);

    // Sharded Services
    // add them here when needed
  }
}
